import { UpgradeOffer } from '@pubstack/common/src/upgrade'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { UPGRADE_OFFER_BENEFITS, UPGRADE_OFFER_CTA, UPGRADE_OFFER_TITLE, upgradeRequestTrackingLabel } from './upgradeOffer'

export type UpgradeDialogCopy = {
  title: string
  /** Outcomes, three at most — the dialog is read standing up, not sat down. */
  benefits: string[]
  cta: string
  trackingLabel: TrackingLabel
}

/**
 * What a scope is told it can have, given what it was just refused.
 *
 * Named after the *offers behind the wall*, not after the page that hit it: an in-app tab and a
 * server-side rule section are the same sale, and pitching them differently would make the same product
 * read as two.
 *
 * Two cases. One product missing gets that product's own words, the ones the tab it gates already uses.
 * Anything else gets the shorter pitch — six bullets across two products is a brochure, and the point here
 * is a conversation.
 *
 * "Anything else" includes naming no product at all, which is the Spark Demand case: it is switched on per
 * scope by the Spark team, so there is no `UpgradeOffer` behind it. It used to get a card of its own here,
 * with no benefits and a Spark-specific headline, which made the one entry in the catalog's Featured row
 * that is not bought off the shelf read as a different kind of thing. Every unavailable entry now asks the
 * same way: this is a pitch for the contract, and "Talk to an expert" is a conversation, not a promise of
 * self-serve.
 */
export const upgradeDialogCopy = (offers: UpgradeOffer[]): UpgradeDialogCopy => {
  if (offers.length === 1) {
    const [offer] = offers
    return { title: UPGRADE_OFFER_TITLE[offer], benefits: UPGRADE_OFFER_BENEFITS[offer], cta: UPGRADE_OFFER_CTA[offer], trackingLabel: upgradeRequestTrackingLabel(offer) }
  }
  return { title: 'Unlock more ad revenue', benefits: BOTH_OFFERS_BENEFITS, cta: 'Talk to an expert', trackingLabel: 'common-upgrade-inventory-request' }
}

/**
 * The One-and-Node pitch in three lines.
 *
 * Not the union of the two products' bullets: those are written to be read one product at a time, and
 * side by side they repeat each other. These say what owning both gets you, which is the one thing
 * neither product's own list can say.
 */
const BOTH_OFFERS_BENEFITS = [
  'Cut client-side latency by moving the auction server-side.',
  'Monetise in-app and AMP inventory from the same ad unit list.',
  'Deploy every stack you run from one place, with no engineering ticket.',
]
